/**
 * "Copy chart.json" from a button, the Export dialog or the palette (W2-09 publish-import; DESIGN.md §5.11 step 2):
 * a chart-only export with its strict self-check put on the clipboard, then a toast. Like exporting the chart file,
 * it first waits briefly for a running checker pass and asks "Export anyway" when the chart file alone has problems
 * that stop Phira from loading it (or the pass did not finish); the clipboard write opened during the click stays
 * open meanwhile. A self-check failure asks before copying; the chart-file switches are either listed next to the
 * button already or offered in a toast.
 */

import type { AppServices } from '../../app/registry/types';
import type { MessageRef } from '../../core/util/types';
import type { DocStore } from '../../state/types';
import { clearForExport } from './clearance';
import { copyChartJson, openClipboard } from './exportFlow';
import type { PublishModel } from './model';
import { formatBytes } from './preflight';
import { m } from './publish.messages';

/** The message of a thrown value (worker errors are plain objects with a message). */
export function errorText(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message !== '') return e.message;
  if (typeof e === 'object' && e !== null && typeof (e as { readonly message?: unknown }).message === 'string') {
    return (e as { readonly message: string }).message;
  }
  return typeof e === 'string' && e !== '' ? e : fallback;
}

/** Progress of the clearance before the copy (the Export dialog shows the wait). */
export interface CopyHooks {
  readonly onWait?: () => void;
  readonly onWaitEnd?: () => void;
  /** The caller went away while waiting for the check: nothing is asked or copied. */
  readonly cancelled?: () => boolean;
}

/**
 * "Copy chart.json" and its toast. `listed`: the caller shows the chart-file switches next to the button
 * (`ChartOnlyNotice`), so the toast only names their count; otherwise (palette, shortcut) a warning toast offers to
 * show them in the Export dialog. Call it synchronously from the click: the clipboard write is opened before
 * anything is awaited (see `openClipboard`).
 */
export async function runCopyChart(
  services: AppServices,
  store: DocStore,
  model: Pick<PublishModel, 'options' | 'prefs'>,
  listed = false,
  hooks: CopyHooks = {},
): Promise<void> {
  const sink = openClipboard();
  let cleared: boolean;
  try {
    cleared = await clearForExport(services, store, true, hooks);
  } catch {
    cleared = false;
  }
  if (!cleared) {
    void sink.deliver(null);
    return;
  }
  const confirmFailed = (_diffs: readonly string[], warnings: readonly MessageRef[]): Promise<boolean> =>
    services.ui.confirm(
      warnings.length > 0
        ? {
            // The file is right but plays differently in Phira (ExportResult.warnings): say what changes.
            title: { key: m.saveWarnedTitle },
            body: warnings[0] ?? { key: m.saveWarnedBody },
            confirm: { key: m.saveAnywayConfirm },
            danger: true,
          }
        : {
            title: { key: m.saveAnywayTitle },
            body: { key: m.saveAnywayBody },
            confirm: { key: m.saveAnywayConfirm },
            danger: true,
          },
    );
  try {
    const out = await copyChartJson(
      services,
      store,
      model.options,
      model.prefs.bakeSpeedForChartOnly,
      confirmFailed,
      sink,
    );
    switch (out.kind) {
      case 'copied': {
        const size = formatBytes(out.bytes, services.i18n);
        if (listed && out.switches.length > 0) {
          services.ui.toast(
            { key: m.toastCopiedNoted, params: { size, count: out.switches.length } },
            { kind: 'success' },
          );
          return;
        }
        services.ui.toast({ key: m.toastCopied, params: { size } }, { kind: 'success' });
        if (out.switches.length > 0) {
          services.ui.toast(
            { key: m.toastCopiedSwitches, params: { count: out.switches.length } },
            {
              kind: 'warning',
              action: { label: { key: m.showSwitches }, command: 'file.export', args: { quick: false } },
            },
          );
        }
        return;
      }
      case 'clipboardRefused':
        services.ui.toast({ key: m.toastCopyFailed }, { kind: 'error' });
        return;
      case 'empty':
        services.ui.toast({ key: m.emptyTitle }, { kind: 'error' });
        return;
      case 'declined':
        return;
    }
  } catch (e) {
    services.ui.toast({ key: m.toastSaveFailed, params: { error: errorText(e, '') } }, { kind: 'error' });
  }
}
